import { useEffect, useState, useCallback } from "react";
import { Star, MessageSquare, Send } from "lucide-react";
import { api } from "../lib/api";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

export default function FeedbackWall() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/feedback");
      setItems(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Load feedback failed:", err?.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/feedback", { message, rating: rating || null });
      setMessage("");
      setRating(0);
      toast.success("Thanks for the feedback!");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  const initials = (name) => (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  return (
    <section className="border border-zinc-800 bg-zinc-900/40" data-testid="feedback-wall">
      <div className="p-6 md:p-8 border-b border-zinc-800">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
          Shape the roadmap
        </div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-3">
          <MessageSquare className="w-6 h-6 text-blue-500" />
          Community feedback
        </h2>
        <p className="text-sm text-zinc-500 mt-2">
          Tell us what to build next. Every note is read.
        </p>
      </div>

      {/* Composer */}
      <form onSubmit={submit} className="p-6 md:p-8 border-b border-zinc-800 space-y-4" data-testid="feedback-form">
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Rate</span>
          <div className="flex items-center gap-1" data-testid="rating-picker" onMouseLeave={() => setHoverRating(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHoverRating(n)}
                onClick={() => setRating(n === rating ? 0 : n)}
                data-testid={`rating-star-${n}`}
                className="p-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
              >
                <Star className={`w-5 h-5 transition-colors ${(hoverRating || rating) >= n ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`} />
              </button>
            ))}
          </div>
        </div>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={`Hey ${user?.name?.split(" ")[0] || "there"} — what could make LinguaSub better?`}
          data-testid="feedback-message"
          maxLength={2000}
          className="min-h-[110px] bg-zinc-950 border-zinc-800 rounded-none text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 resize-none"
        />
        <div className="flex items-center justify-between gap-4">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            {message.length}/2000
          </span>
          <Button
            type="submit"
            disabled={submitting || !message.trim()}
            data-testid="feedback-submit"
            className="h-10 bg-white text-zinc-950 hover:bg-zinc-200 disabled:bg-zinc-800 disabled:text-zinc-500 font-semibold rounded-none transition-colors duration-200"
          >
            <Send className="w-4 h-4 mr-2" />
            {submitting ? "Sending..." : "Post feedback"}
          </Button>
        </div>
      </form>

      {/* Wall */}
      <div className="max-h-[520px] overflow-y-auto divide-y divide-zinc-800" data-testid="feedback-list">
        {items.length === 0 && (
          <div className="p-8 text-center">
            <div className="text-sm text-zinc-500">Be the first to share what LinguaSub should learn next.</div>
          </div>
        )}
        {items.map((f) => (
          <article key={f.feedback_id} className="p-6 md:px-8 hover:bg-zinc-900/60 transition-colors duration-150" data-testid="feedback-item">
            <div className="flex items-start gap-3">
              {f.user_picture ? (
                <img src={f.user_picture} alt={f.user_name} className="w-9 h-9 rounded-full border border-zinc-800 flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full border border-zinc-800 bg-zinc-900 flex items-center justify-center text-[10px] font-mono text-zinc-400 flex-shrink-0">
                  {initials(f.user_name)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-sm text-white font-semibold truncate">{f.user_name}</span>
                  {f.rating && (
                    <span className="flex items-center gap-0.5 text-amber-400">
                      {Array.from({ length: f.rating }).map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-amber-400" />
                      ))}
                    </span>
                  )}
                  <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    {new Date(f.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words">{f.message}</p>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
